import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import WebApp from "@twa-dev/sdk";
import type { Invite, InviteStatus } from "../../types";
import { createInvite, fetchInvites, revokeInvite } from "../../utils/api";
import { copyText } from "../../utils/copyText";
import { useAppConfig } from "../../config/AppConfigContext";
import { AppHeader, Banner, Icon, SectionHead } from "../common/ui";

/**
 * Single-use invite links (admin only).
 *
 * A link carries a set of blocks and a number of days. Whoever opens it first
 * gets that access — a new user on sign-up, an existing one straight away —
 * through Telegram or a browser. The rules live server-side in
 * services/invites.py; this screen only creates, lists and revokes.
 *
 * Two links per invite, same code:
 *   - Telegram: t.me/<bot>?start=inv_<code> — the bot activates it in the chat;
 *   - browser:  <this site>/?invite=<code> — activated after Google/Apple sign-in.
 */
const DAY_PRESETS = [30, 90, 180, 365];

const STATUS_BADGE: Record<InviteStatus, string> = {
  active: "badge badge-success",
  redeemed: "badge badge-violet",
  expired: "badge",
  revoked: "badge",
};

function webLink(code: string): string {
  return `${window.location.origin}/?invite=${encodeURIComponent(code)}`;
}

function openShare(link: string, text: string) {
  const url = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`;
  try {
    if (WebApp.initData) { WebApp.openTelegramLink(url); return; }
  } catch { /* not inside Telegram */ }
  window.open(url, "_blank", "noopener");
}

function fmtDate(iso: string | null | undefined, lang: string): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(lang.startsWith("en") ? "en-GB" : "ru-RU", {
    day: "2-digit", month: "2-digit", year: "numeric",
  });
}

/** One copyable link row: label, the link itself, and a copy button that confirms. */
function LinkRow({ label, link }: { label: string; link: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState<"ok" | "fail" | null>(null);

  const copy = async () => {
    const ok = await copyText(link);
    setCopied(ok ? "ok" : "fail");
    setTimeout(() => setCopied(null), 1800);
  };

  return (
    <div className="invite-link">
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="t-xs muted-2" style={{ fontWeight: 700 }}>{label}</div>
        <div className="invite-link__url">{link}</div>
      </div>
      <button className="btn btn-glass invite-link__copy" onClick={copy} aria-label={t("invite.copy")}>
        <Icon name={copied === "ok" ? "check" : "copy"} size={16} />
        <span>{copied === "ok" ? t("invite.copied") : copied === "fail" ? t("invite.copyFailed") : t("invite.copy")}</span>
      </button>
    </div>
  );
}

function InviteLinks({ invite }: { invite: Invite }) {
  const { t } = useTranslation();
  return (
    <div className="stack" style={{ "--gap": "8px" } as React.CSSProperties}>
      {invite.telegram_link && <LinkRow label={t("invite.linkTelegram")} link={invite.telegram_link} />}
      <LinkRow label={t("invite.linkWeb")} link={webLink(invite.code)} />
      <button
        className="btn btn-glass btn-block"
        onClick={() => openShare(invite.telegram_link || webLink(invite.code), t("invite.shareText"))}
      >
        <Icon name="send" size={16} /> {t("invite.shareTelegram")}
      </button>
    </div>
  );
}

export default function AdminInvites({
  token, onExit, theme, onThemeChange,
}: {
  token: string;
  onExit: () => void;
  theme: "dark" | "light";
  onThemeChange: (t: "dark" | "light") => void;
}) {
  const { t, i18n } = useTranslation();
  const isRu = !i18n.language?.startsWith("en");
  const { config } = useAppConfig();
  const blocks = config.blocks ?? [];

  const [picked, setPicked] = useState<Set<string>>(new Set(["base"]));
  const [days, setDays] = useState(30);
  const [validDays, setValidDays] = useState(30);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "error" | "success"; text: string } | null>(null);

  const [invites, setInvites] = useState<Invite[] | null>(null);
  const [botUsername, setBotUsername] = useState<string | null>(null);
  const [fresh, setFresh] = useState<Invite | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [filter, setFilter] = useState<"active" | "all">("active");

  const load = useCallback(async () => {
    try {
      const res = await fetchInvites(token);
      setInvites(res.invites);
      setBotUsername(res.bot_username ?? null);
    } catch (e) {
      setInvites([]);
      setMsg({ kind: "error", text: e instanceof Error ? e.message : t("common.error") });
    }
  }, [token, t]);

  useEffect(() => { load(); }, [load]);

  const toggle = (key: string) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const create = async () => {
    if (!picked.size || days < 1) return;
    setBusy(true);
    setMsg(null);
    try {
      // Keep the canonical block order, not the order they were ticked in.
      const ordered = blocks.map((b) => b.key).filter((k) => picked.has(k));
      const inv = await createInvite(
        { blocks: ordered, days, valid_days: validDays, note: note.trim() || undefined }, token,
      );
      setFresh(inv);
      setNote("");
      setInvites((prev) => [inv, ...(prev ?? [])]);
    } catch (e) {
      setMsg({ kind: "error", text: e instanceof Error ? e.message : t("common.error") });
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (inv: Invite) => {
    if (!window.confirm(t("invite.revokeConfirm"))) return;
    try {
      const updated = await revokeInvite(inv.id, token);
      setInvites((prev) => (prev ?? []).map((x) => (x.id === updated.id ? updated : x)));
      if (fresh?.id === updated.id) setFresh(null);
    } catch (e) {
      setMsg({ kind: "error", text: e instanceof Error ? e.message : t("common.error") });
    }
  };

  const shown = useMemo(
    () => (invites ?? []).filter((i) => filter === "all" || i.status === "active"),
    [invites, filter],
  );
  const counts = useMemo(() => {
    const c = { active: 0, redeemed: 0 };
    for (const i of invites ?? []) {
      if (i.status === "active") c.active += 1;
      if (i.status === "redeemed") c.redeemed += 1;
    }
    return c;
  }, [invites]);

  const blockNames = (inv: Invite) => inv.blocks.map((b) => (isRu ? b.title_ru : b.title_en)).join(", ");

  return (
    <div className="beeapp__scroll">
      <AppHeader theme={theme} onThemeChange={onThemeChange} onBack={onExit} title={t("invite.adminTitle")} />
      <div className="wrap content-cap" style={{ paddingTop: 6 }}>
        <div className="stack" style={{ "--gap": "18px" } as React.CSSProperties}>
          {msg && <Banner kind={msg.kind}>{msg.text}</Banner>}

          {/* ── Create ── */}
          <section className="glass glass-lg" style={{ padding: 20 }}>
            <SectionHead icon="ticket">{t("invite.createTitle")}</SectionHead>
            <p className="t-xs muted-2" style={{ margin: "0 0 14px", lineHeight: 1.45 }}>
              {t("invite.createHint")}
            </p>

            <p className="t-sm" style={{ fontWeight: 700, marginBottom: 8 }}>{t("invite.blocks")}</p>
            <div className="stack" style={{ "--gap": "7px" } as React.CSSProperties}>
              {blocks.map((b) => {
                const on = picked.has(b.key);
                return (
                  <button
                    key={b.key}
                    onClick={() => toggle(b.key)}
                    role="switch"
                    aria-checked={on}
                    className={"invite-block" + (on ? " is-on" : "")}
                  >
                    <span className="invite-block__check"><Icon name="check" size={13} stroke={3} /></span>
                    <span className="t-sm" style={{ fontWeight: 700 }}>{isRu ? b.title_ru : b.title_en}</span>
                  </button>
                );
              })}
            </div>

            <div className="invite-fields">
              <div>
                <label>
                  <span className="t-xs muted-2">{t("invite.accessDays")}</span>
                  <input
                    type="number" min={1} max={3650} value={days} inputMode="numeric"
                    onChange={(e) => setDays(Math.max(0, parseInt(e.target.value, 10) || 0))}
                    className="invite-input"
                  />
                </label>
                <div className="row" style={{ gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                  {DAY_PRESETS.map((d) => (
                    <button
                      key={d}
                      className={"chip-btn" + (days === d ? " is-on" : "")}
                      onClick={() => setDays(d)}
                      aria-pressed={days === d}
                    >
                      {t("invite.days", { count: d })}
                    </button>
                  ))}
                </div>
              </div>
              <label>
                <span className="t-xs muted-2">{t("invite.validDays")}</span>
                <input
                  type="number" min={1} max={365} value={validDays} inputMode="numeric"
                  onChange={(e) => setValidDays(Math.min(365, Math.max(0, parseInt(e.target.value, 10) || 0)))}
                  className="invite-input"
                />
              </label>
            </div>

            <label style={{ display: "block", marginTop: 12 }}>
              <span className="t-xs muted-2">{t("invite.note")}</span>
              <input
                type="text" maxLength={120} value={note} placeholder={t("invite.notePlaceholder")}
                onChange={(e) => setNote(e.target.value)}
                className="invite-input"
                style={{ fontWeight: 500 }}
              />
            </label>
            <p className="t-xs muted-2" style={{ margin: "8px 0 0", lineHeight: 1.45 }}>
              {t("invite.rulesHint", { count: validDays })}
            </p>

            <button
              className="btn btn-primary btn-block"
              onClick={create}
              disabled={busy || !picked.size || days < 1 || validDays < 1}
              style={{ marginTop: 14 }}
            >
              {busy ? <span className="spinner" /> : <><Icon name="link" size={17} /> {t("invite.create")}</>}
            </button>

            {fresh && (
              <div className="invite-fresh">
                <div className="row" style={{ gap: 8, marginBottom: 10 }}>
                  <Icon name="checkCircle" size={18} />
                  <span className="t-sm" style={{ fontWeight: 800 }}>{t("invite.created")}</span>
                </div>
                <p className="t-xs muted-2" style={{ margin: "0 0 10px" }}>
                  {blockNames(fresh)} · {t("invite.days", { count: fresh.days })}
                  {fresh.note ? ` · ${fresh.note}` : ""}
                </p>
                <InviteLinks invite={fresh} />
                {!botUsername && (
                  <p className="t-xs muted-2" style={{ margin: "8px 0 0" }}>{t("invite.noBot")}</p>
                )}
              </div>
            )}
          </section>

          {/* ── List ── */}
          <section className="glass glass-lg" style={{ padding: 20 }}>
            <div className="between" style={{ marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
              <SectionHead icon="history">{t("invite.listTitle")}</SectionHead>
              <div className="seg" role="group" aria-label={t("invite.listTitle")} style={{ marginBottom: 12 }}>
                <button aria-pressed={filter === "active"} onClick={() => setFilter("active")}>
                  {t("invite.filterActive")} · {counts.active}
                </button>
                <button aria-pressed={filter === "all"} onClick={() => setFilter("all")}>
                  {t("invite.filterAll")}
                </button>
              </div>
            </div>

            {invites === null ? (
              <div className="spinner spinner-amber" style={{ margin: "20px auto" }} />
            ) : shown.length === 0 ? (
              <p className="t-sm muted" style={{ margin: 0 }}>
                {filter === "active" ? t("invite.emptyActive") : t("invite.empty")}
              </p>
            ) : (
              <div className="stack" style={{ "--gap": "9px" } as React.CSSProperties}>
                {shown.map((inv) => {
                  const open = openId === inv.id;
                  return (
                    <div key={inv.id} className="invite-row">
                      <button
                        className="invite-row__head"
                        onClick={() => setOpenId(open ? null : inv.id)}
                        aria-expanded={open}
                      >
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                            <span className={STATUS_BADGE[inv.status]}>{t(`invite.status.${inv.status}`)}</span>
                            <span className="t-sm" style={{ fontWeight: 700 }}>
                              {inv.note || blockNames(inv)}
                            </span>
                          </span>
                          <span className="t-xs muted-2" style={{ display: "block", marginTop: 5 }}>
                            {inv.note ? `${blockNames(inv)} · ` : ""}
                            {t("invite.days", { count: inv.days })}
                            {" · "}
                            {inv.status === "redeemed"
                              ? t("invite.redeemedBy", {
                                  who: inv.redeemed_by || "—",
                                  date: fmtDate(inv.redeemed_at, i18n.language),
                                  via: inv.redeemed_via === "telegram" ? "Telegram" : t("invite.viaWeb"),
                                })
                              : inv.status === "active"
                                ? t("invite.validUntil", { date: fmtDate(inv.expires_at, i18n.language) })
                                : t("invite.createdOn", { date: fmtDate(inv.created_at, i18n.language) })}
                          </span>
                        </span>
                        {inv.status === "active" && (
                          <span style={{ color: "var(--text-3)", transform: open ? "rotate(180deg)" : "none", transition: "transform .25s" }}>
                            <Icon name="chevronDown" size={16} />
                          </span>
                        )}
                      </button>

                      {open && inv.status === "active" && (
                        <div style={{ padding: "0 12px 12px" }}>
                          <InviteLinks invite={inv} />
                          <button className="btn btn-ghost btn-block" onClick={() => revoke(inv)} style={{ marginTop: 6 }}>
                            <Icon name="trash" size={16} /> {t("invite.revoke")}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {counts.redeemed > 0 && filter === "active" && (
              <p className="t-xs muted-2" style={{ margin: "12px 0 0" }}>
                {t("invite.redeemedCount", { count: counts.redeemed })}
              </p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
